import React, { useCallback, useMemo, useState } from 'react';
import { Box, List, PaginationBar, RawButton, SearchInput, SelectDropdown, Text } from '@common-fe/common-fe';

import { DefaultSortTypesEnum } from '@/common/types';

import { useGetFieldLevelAccessListQuery } from '../queries';

import FieldLevelAccessCopyModal from './FieldLevelAccessCopyModal';
import FieldLevelAccessListItem from './FieldLevelAccessListItem';
import FieldLevelAccessSummaryModal from './FieldLevelAccessSummaryModal';
import { useFieldLevelAccessListParams } from './hooks';

const SORTING_OPTIONS = [
  {
    key: DefaultSortTypesEnum.ASC,
    value: DefaultSortTypesEnum.ASC,
    title: 'A-Z',
  },
  {
    key: DefaultSortTypesEnum.DESC,
    value: DefaultSortTypesEnum.DESC,
    title: 'Z-A',
  },
];

const FieldLevelAccessList = () => {
  const [summaryItemId, setSummaryItemId] = useState('');
  const [copyModalItemId, setCopyModalItemId] = useState('');
  const [clearId, setClearId] = useState('');
  const { page, setPerPage, perPage, searchString, setSearchString, setPage, sortBy, setSortBy } =
    useFieldLevelAccessListParams();

  const handleResetFilters = useCallback(() => {
    setPage(1);
    setSortBy(DefaultSortTypesEnum.ASC);
    setSearchString('');
    setClearId(`${+new Date()}`);
  }, [setPage, setSearchString, setSortBy]);

  const isShowedResetButton = useMemo(() => {
    return page !== 1 || sortBy !== DefaultSortTypesEnum.ASC || !!searchString.length;
  }, [page, searchString.length, sortBy]);

  const { isLoading, total, data } = useGetFieldLevelAccessListQuery({
    sortBy: sortBy as DefaultSortTypesEnum,
    page,
    perPage,
    searchString,
  });

  return (
    <Box
      data-testid="roles-list"
      direction="column"
      pad="l"
      background="module"
      round="moduleRound"
      style={{ position: 'relative' }}
    >
      <Box
        align="center"
        style={{
          position: 'absolute',
          bottom: '100%',
          left: 0,
          right: 0,
        }}
      >
        {isShowedResetButton && (
          <RawButton onClick={handleResetFilters}>
            <Box
              round={{ corner: 'top', size: 'button2Round' }}
              background="module"
              pad={{ horizontal: '8px', vertical: '2px' }}
            >
              <Text color="textBody" weight={400}>
                Clear All
              </Text>
            </Box>
          </RawButton>
        )}
      </Box>
      <FieldLevelAccessSummaryModal
        id={summaryItemId}
        key={`${isLoading}`}
        onChangeId={setSummaryItemId}
        onCopyModalOpen={setCopyModalItemId}
      />
      <FieldLevelAccessCopyModal key={`${isLoading}`} id={copyModalItemId} onChangeId={setCopyModalItemId} />

      <Box key={clearId} direction="row" justify="between" align="center" margin={{ bottom: 'm' }}>
        <Box>
          <Text weight="bold">Entities: {total || 0}</Text>
        </Box>
        <Box direction="row">
          <Box width="240px" margin={{ right: 's' }}>
            <SearchInput
              data-testid="field-level-access-list_search"
              placeholder="Search"
              hasSearchingHistory
              testId="field-level-access-search"
              value={searchString}
              onChange={setSearchString}
            />
          </Box>
          <Box width={{ min: 'control' }}>
            <SelectDropdown
              id="sort_by"
              testId="sort_field_level_access"
              disabled={false}
              name="Sort by"
              prefix="Sort by"
              singleMode
              value={sortBy}
              onChange={setSortBy}
              activeTitle
              options={SORTING_OPTIONS}
            />
          </Box>
        </Box>
      </Box>
      <List>
        <List.Header>
          <List.Header.Item flex={2}>Name/Description</List.Header.Item>
          <List.Header.Item flex={4}>Applied to</List.Header.Item>
          <Box width="24px"></Box>
        </List.Header>
        {isLoading ? (
          <List.Preloader count={10} />
        ) : (
          data.map((item) => (
            <FieldLevelAccessListItem
              key={item.id}
              {...item}
              onSetCopyModal={setCopyModalItemId}
              onOpenSummary={setSummaryItemId}
            />
          ))
        )}
        {!isLoading && data.length === 0 ? <List.Placeholder /> : null}

        <PaginationBar
          page={page}
          total={total || 0}
          pageSize={perPage}
          onChangePage={setPage}
          onChangePageSize={setPerPage}
        />
      </List>
    </Box>
  );
};
export default FieldLevelAccessList;
